import * as React from "react";
import type { AddInHistoryMessage, AddInHistorySuggestion, SaveAndInsertSuggestionArgs } from "../../types/chat";
import { ChatAssistantBubble } from "./ChatAssistantBubble";
import { ChatEmptyState } from "./ChatEmptyState";
import { ChatTypingIndicator } from "./ChatTypingIndicator";
import { ChatUserBubble } from "./ChatUserBubble";
import { ChatSpinner } from "./ui/ChatSpinner";

const LOAD_OLDER_EDGE_PX = 80;
const LOAD_OLDER_REARM_SCROLL_PX = 96;
const LOAD_OLDER_MIN_GAP_MS = 450;

export type ChatTranscriptProps = {
  messages: AddInHistoryMessage[];
  historyLoading: boolean;
  historyLoadingMore: boolean;
  historyExhausted: boolean;
  onLoadOlder: () => Promise<void>;
  loading: boolean;
  retryingAssistantId?: string | null;
  error: string | null;
  onInsertSuggestion: (suggestion: AddInHistorySuggestion, suggestionIndex: number) => void | Promise<void>;
  onSaveAndInsertSuggestion: (args: SaveAndInsertSuggestionArgs) => Promise<void>;
  hasSelectedText: boolean;
  onRetryAssistantMessage?: (message: AddInHistoryMessage) => void | Promise<void>;
  onSuggestionClick: (text: string) => void;
  /** When set, empty state shows this instead of suggestion chips. */
  accessDeniedMessage?: string | null;
  accessLoading?: boolean;
};

export const ChatTranscript: React.FC<ChatTranscriptProps> = ({
  messages,
  historyLoading,
  historyLoadingMore,
  historyExhausted,
  onLoadOlder,
  loading,
  retryingAssistantId = null,
  error,
  onInsertSuggestion,
  onSaveAndInsertSuggestion,
  hasSelectedText,
  onRetryAssistantMessage,
  onSuggestionClick,
  accessDeniedMessage = null,
  accessLoading = false,
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const loadOlderLockRef = React.useRef(false);
  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [editedTextByKey, setEditedTextByKey] = React.useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!hasSelectedText && editingKey !== null && busyKey === null) {
      setEditingKey(null);
      setEditedTextByKey({});
    }
  }, [hasSelectedText, editingKey, busyKey]);

  const loadOlderArmedRef = React.useRef(true);
  const lastLoadOlderStartMsRef = React.useRef(0);
  const pendingScrollRestoreRef = React.useRef<{ prevHeight: number; prevTop: number } | null>(null);
  const prevLoadingRef = React.useRef(false);
  const prevHistoryLoadingRef = React.useRef(false);

  React.useEffect(() => {
    if (messages.length === 0 && !historyLoading) {
      loadOlderArmedRef.current = true;
      lastLoadOlderStartMsRef.current = 0;
    }
  }, [messages.length, historyLoading]);

  React.useLayoutEffect(() => {
    if (loading) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [loading]);

  React.useEffect(() => {
    if (prevHistoryLoadingRef.current && !historyLoading && scrollRef.current && messages.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevHistoryLoadingRef.current = historyLoading;
  }, [historyLoading, messages.length]);

  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    const pending = pendingScrollRestoreRef.current;
    if (!el || !pending || historyLoadingMore) return;
    const newHeight = el.scrollHeight;
    el.scrollTop = newHeight - pending.prevHeight + pending.prevTop;
    pendingScrollRestoreRef.current = null;
  }, [historyLoadingMore, messages.length]);

  React.useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el || historyLoadingMore || historyExhausted || historyLoading) return;

    const hasOverflow = el.scrollHeight > el.clientHeight + 2;
    if (hasOverflow && el.scrollTop > LOAD_OLDER_REARM_SCROLL_PX) {
      loadOlderArmedRef.current = true;
    }
    if (!hasOverflow) loadOlderArmedRef.current = true;

    if (el.scrollTop >= LOAD_OLDER_EDGE_PX) return;
    if (!loadOlderArmedRef.current) return;
    if (loadOlderLockRef.current) return;

    const now = Date.now();
    if (now - lastLoadOlderStartMsRef.current < LOAD_OLDER_MIN_GAP_MS) return;

    lastLoadOlderStartMsRef.current = now;
    loadOlderArmedRef.current = false;
    loadOlderLockRef.current = true;
    pendingScrollRestoreRef.current = { prevHeight: el.scrollHeight, prevTop: el.scrollTop };

    void onLoadOlder()
      .catch(() => {
        pendingScrollRestoreRef.current = null;
        loadOlderArmedRef.current = true;
      })
      .finally(() => {
        loadOlderLockRef.current = false;
      });
  }, [historyLoading, historyLoadingMore, historyExhausted, onLoadOlder]);

  const lastAssistantMessageId = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "assistant") return messages[i].id;
    }
    return null;
  }, [messages]);

  React.useEffect(() => {
    if (busyKey !== null) return;
    if (
      loading ||
      (editingKey && lastAssistantMessageId && !editingKey.startsWith(`${lastAssistantMessageId}-s-`))
    ) {
      setEditingKey(null);
      setEditedTextByKey({});
    }
  }, [lastAssistantMessageId, loading, editingKey, busyKey]);

  const showBottomLoading = loading && !retryingAssistantId;
  const showEmpty =
    messages.length === 0 &&
    !historyLoading &&
    !loading &&
    !error &&
    !accessLoading;

  return (
    <div ref={scrollRef} onScroll={handleScroll} className="chat-transcript thin-scroll">
      {historyLoadingMore ? (
        <div className="chat-transcript__load-older">
          <ChatSpinner size="sm" />
        </div>
      ) : null}

      {historyExhausted && messages.length > 0 ? (
        <span className="chat-transcript__caption">Beginning of conversation</span>
      ) : null}

      {(historyLoading || accessLoading) && messages.length === 0 ? (
        <div className="chat-transcript__loading">
          <ChatSpinner size="md" />
          <span>{accessLoading ? "Checking access…" : "Loading messages…"}</span>
        </div>
      ) : null}

      {showEmpty ? (
        <ChatEmptyState
          onSuggestionClick={onSuggestionClick}
          hasSelectedText={hasSelectedText}
          accessDeniedMessage={accessDeniedMessage}
        />
      ) : null}

      {messages.map((m, messageIndex) =>
        m.role === "user" ? (
          <ChatUserBubble
            key={m.id}
            content={m.content}
            selectedText={m.selectedText}
            createdAt={m.createdAt}
          />
        ) : (
          <ChatAssistantBubble
            key={m.id}
            message={m}
            messageIndex={messageIndex}
            isLatestAssistant={m.id === lastAssistantMessageId}
            loading={loading}
            retrying={retryingAssistantId === m.id}
            hasSelectedText={hasSelectedText}
            onInsertSuggestion={onInsertSuggestion}
            onSaveAndInsertSuggestion={onSaveAndInsertSuggestion}
            onRetryAssistantMessage={onRetryAssistantMessage}
            editingKey={editingKey}
            editedTextByKey={editedTextByKey}
            busyKey={busyKey}
            onEnterEdit={(key, plain) => {
              setEditingKey(key);
              setEditedTextByKey((prev) => ({ ...prev, [key]: prev[key] ?? plain }));
            }}
            onExitEdit={(key) => {
              setEditingKey((prev) => (prev === key ? null : prev));
              setEditedTextByKey((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
              });
            }}
            onEditedTextChange={(key, value) => {
              setEditedTextByKey((prev) => ({ ...prev, [key]: value }));
            }}
            onBusy={setBusyKey}
          />
        ),
      )}

      {error ? (
        <span className="chat-transcript__error" role="alert">
          {error}
        </span>
      ) : null}

      {showBottomLoading ? <ChatTypingIndicator /> : null}
    </div>
  );
};
